import { useContext, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Icon from "../components/icon";
import Question from "../components/question";
import UserContext from "../context/user";
import { computePartial } from "../utils/partial";
import useConditionalQuestions from "../hooks/useConditionalQuestions";

const Form = ({
  section,
  survey,
  partial,
  currentSectionIndex,
  handlePrevious,
  handleNext,
  handleSuccessSubmit,
  formData,
  setFormData,
  readOnlyMode,
  labels,
  locale,
}) => {
  const { callApi } = useContext(UserContext);
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    setValue,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    mode: "onTouched",
    defaultValues: useMemo(() => formData, [formData]),
  });

  const formWatch = watch();

  useEffect(() => reset(formData), [reset, formData]);

  const pathDriverQuestion = useMemo(
    () =>
      section.questions.find((question) =>
        question.answers.find((answer) =>
          ["GO_TO_SECTION", "SUBMIT_FORM"].includes(answer.nextSectionType)
        )
      ),
    [section]
  );

  const pathDriverAnswer = useMemo(() => {
    if (pathDriverQuestion?.id && formWatch[`${pathDriverQuestion.id}`]) {
      const driverAnswer = pathDriverQuestion.answers.find(
        (answer) => String(answer.id) === formWatch[`${pathDriverQuestion?.id}`]
      );
      /* console.log(
        "Path driver answer encountered for question",
        pathDriverQuestion?.id,
        ", behaviour: ",
        driverAnswer?.nextSectionType,
        driverAnswer?.nextSectionId
      ); */
      return driverAnswer;
    }
  }, [pathDriverQuestion, formWatch]);

  const nextIsSubmit = useMemo(
    () =>
      pathDriverAnswer?.nextSectionType === "SUBMIT_FORM" ||
      section.nextSectionType === "SUBMIT_FORM" ||
      currentSectionIndex === survey?.definition?.sections.length - 1,
    [section, currentSectionIndex, survey, pathDriverAnswer]
  );

  const nextIsGoToSection = useMemo(
    () =>
      pathDriverAnswer?.nextSectionType === "GO_TO_SECTION"
        ? pathDriverAnswer?.nextSectionId
        : section.nextSectionType === "GO_TO_SECTION"
        ? section.nextSectionId
        : null,
    [section, pathDriverAnswer]
  );

  const partialMutation = useMutation(
    ({ survey, values, partial }) =>
      callApi(
        partial
          ? `/survey/${survey.contentId}/answer/update-partial/${partial}`
          : `/survey/${survey.contentId}/answer/save-partial`,
        {
          method: partial ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(computePartial(survey, { ...formData, ...values }, partial)),
        }
      ),
    {
      onSuccess: (data) => {
        console.log(
          `Partial ${partial ? "updated" : "saved"}: {submissionId: ${
            data?.submissionId
          }}. Next section id: ${survey?.definition?.sections[currentSectionIndex + 1]?.id}`
        );
      },
    }
  );

  const submitMutation = useMutation(
    ({ survey, submissionId }) =>
      callApi(`/survey/${survey.contentId}/submit-answer/${submissionId}`, { method: "PUT" }),
    {
      onSuccess: () => {
        console.log("invalidating", survey.contentId);
        queryClient.invalidateQueries(["answers", `${survey.contentId}`]);
        handleSuccessSubmit();
      },
    }
  );

  const onSubmit = async (values) => {
    if (nextIsSubmit) {
      const { submissionId } = await partialMutation.mutateAsync({ survey, values, partial });
      await submitMutation.mutateAsync({ survey, submissionId });
    } else {
      const { submissionId } = await partialMutation.mutateAsync({ survey, values, partial });
      handleNext(submissionId, nextIsGoToSection);
    }
    setFormData((current) => ({ ...current, ...values }));
  };

  const onReadOnlySubmit = () => {
    if (nextIsSubmit) {
      handleSuccessSubmit();
    } else {
      handleNext(null, nextIsGoToSection);
    }
  };

  const { filteredQuestions } = useConditionalQuestions(section?.questions, formWatch, setValue);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="form" key={section.id} noValidate>
      {filteredQuestions.map((question) => (
        <Question
          question={question}
          key={question.id}
          register={register}
          error={errors[`${question.id}`]}
          touched={touchedFields[`${question.id}`]}
          isDirty={dirtyFields[`${question.id}`]}
          control={control}
          trigger={trigger}
          readOnlyMode={readOnlyMode}
          labels={labels}
          locale={locale}
        />
      ))}
      <div className="form__buttons">
        {currentSectionIndex > 0 && (
          <button className="link" onClick={handlePrevious} type="button">
            <Icon name="angle-down" />
            {labels?.WELFARE_FORM_BTN_PREVIOUS || "Previous"}
          </button>
        )}
        {readOnlyMode ? (
          <button className="button primary form__submit" type="button" onClick={onReadOnlySubmit}>
            {nextIsSubmit
              ? labels?.WELFARE_END_REVIEW || "End review"
              : labels?.WELFARE_FORM_BTN_NEXT || "Next"}
          </button>
        ) : (
          <button className="button primary form__submit" disabled={partialMutation.isLoading}>
            {partialMutation.isLoading && <span className="loading-spinner" />}
            {nextIsSubmit
              ? labels?.WELFARE_FORM_BTN_SUBMIT || "Submit"
              : labels?.WELFARE_FORM_BTN_NEXT || "Next"}
          </button>
        )}
      </div>
    </form>
  );
};

export default Form;
